import React, { useState, useEffect, useRef } from 'react';
import type { AchievementGroup, Achievement, AchievementsPageLabels } from '../../shared/types/achievements';

export interface AchievementsPageProps {
  groups: AchievementGroup[];
  totalCount: number;
  unlockedCount: number;
  progressPercentage: number;
  goalsUrl: string;
  highlightAchievement: string | null;
  labels: AchievementsPageLabels;
}

const MAX_VISIBLE = 6;

export function AchievementsPage({
  groups,
  totalCount,
  unlockedCount,
  progressPercentage,
  goalsUrl,
  highlightAchievement,
  labels,
}: AchievementsPageProps) {
  const highlightRef = useRef<HTMLDivElement>(null);

  // Scroll to highlighted achievement on mount
  useEffect(() => {
    if (highlightAchievement && highlightRef.current) {
      highlightRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [highlightAchievement]);

  const isEmpty = groups.length === 0 || groups.every(g => g.achievements.length === 0);

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0">{labels.title}</h2>
        <a href={goalsUrl} className="btn btn-outline-primary">
          <i className="bi bi-bullseye me-1"></i>
          {labels.goalsButton}
        </a>
      </div>

      {/* Empty state */}
      {isEmpty && (
        <div className="text-center py-5 bg-light rounded-4 mb-5 border border-2 border-opacity-10">
          <i className="bi bi-trophy display-1 text-warning opacity-50"></i>
          <p className="mt-3 text-muted fs-5">{labels.emptyMessage}</p>
        </div>
      )}

      {/* Achievement Groups */}
      {groups.map((group, groupIndex) => (
        <AchievementGroupSection
          key={groupIndex}
          group={group}
          highlightAchievement={highlightAchievement}
          highlightRef={highlightRef}
          labels={labels}
        />
      ))}

      {/* Progress Section */}
      {unlockedCount > 0 && (
        <div className="card shadow-sm rounded-4 border-0 mt-4" style={{ borderLeft: '4px solid var(--bs-primary)' }}>
          <div className="card-body p-3 p-sm-4">
            <div className="row align-items-center">
              <div className="col-8 col-md-8">
                <h5 className="mb-2 fw-bold text-primary">{labels.progress}</h5>
                <p className="mb-3 text-muted">
                  <span className="fs-4 fw-bold text-secondary">{unlockedCount}</span> / {totalCount} {labels.achievementsUnlocked}
                </p>
                <div className="progress bg-light" style={{ height: 12, borderRadius: 8 }}>
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{
                      width: `${progressPercentage}%`,
                      borderRadius: 8,
                      background: 'linear-gradient(90deg, var(--bs-primary) 0%, #667eea 100%)',
                    }}
                    aria-valuenow={unlockedCount}
                    aria-valuemin={0}
                    aria-valuemax={totalCount}
                  />
                </div>
              </div>
              <div className="col-4 col-md-4 text-center text-md-end">
                <div className="display-4 fw-bold text-secondary">{progressPercentage}%</div>
                <i className="bi bi-trophy-fill display-5 text-secondary opacity-25 d-none d-sm-inline"></i>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface AchievementGroupSectionProps {
  group: AchievementGroup;
  highlightAchievement: string | null;
  highlightRef: React.RefObject<HTMLDivElement>;
  labels: AchievementsPageLabels;
}

function AchievementGroupSection({
  group,
  highlightAchievement,
  highlightRef,
  labels,
}: AchievementGroupSectionProps) {
  const [expanded, setExpanded] = useState(false);
  const hasMore = group.achievements.length > MAX_VISIBLE;
  const visibleAchievements = expanded ? group.achievements : group.achievements.slice(0, MAX_VISIBLE);

  return (
    <div className="mb-5">
      <h4 className="mb-4 fw-bold text-primary">{group.title}</h4>
      <div className="row g-3 g-md-4">
        {visibleAchievements.map((achievement) => (
          <div
            key={achievement.key}
            className="col-12 col-sm-6 col-lg-4"
            ref={achievement.key === highlightAchievement ? highlightRef : undefined}
          >
            <AchievementCard
              achievement={achievement}
              isHighlighted={achievement.key === highlightAchievement}
              labels={labels}
            />
          </div>
        ))}
      </div>

      {hasMore && (
        <div className="text-center mt-4">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setExpanded(!expanded)}
          >
            <span>
              {expanded ? labels.showLess : `${labels.showMore} (${group.achievements.length - MAX_VISIBLE})`}
            </span>
            <i className={`bi bi-chevron-${expanded ? 'up' : 'down'} ms-1`}></i>
          </button>
        </div>
      )}
    </div>
  );
}

interface AchievementCardProps {
  achievement: Achievement;
  isHighlighted: boolean;
  labels: AchievementsPageLabels;
}

function AchievementCard({ achievement, isHighlighted, labels }: AchievementCardProps) {
  const isUnlocked = achievement.unlocked;

  return (
    <div
      className={`card h-100 ${isHighlighted ? 'shadow-lg border-warning border-3' : 'shadow-sm'}`}
      id={`achievement-${achievement.key}`}
      style={{
        transition: 'all 0.3s ease',
        animation: isHighlighted ? 'highlight-pulse 2s ease-in-out' : undefined,
      }}
    >
      <div className="card-body text-center p-3 p-sm-4">
        {/* Achievement Icon */}
        <div className="position-relative d-inline-block mb-3">
          <div
            className={`rounded-circle d-flex align-items-center justify-content-center mx-auto ${
              isUnlocked
                ? 'bg-warning bg-opacity-10 border border-warning border-3'
                : 'bg-light border border-2'
            }`}
            style={{
              width: 100,
              height: 100,
              minWidth: 100,
              minHeight: 100,
              flexShrink: 0,
              transition: 'all 0.3s ease',
            }}
          >
            <i
              className={`bi bi-${achievement.icon} ${isUnlocked ? 'text-warning' : 'text-muted'}`}
              style={{ fontSize: '2.5rem' }}
            ></i>
          </div>
          {isUnlocked ? (
            <span
              className="position-absolute bottom-0 end-0 badge rounded-circle bg-success shadow p-2"
              style={{ fontSize: '0.875rem' }}
            >
              <i className="bi bi-check-lg"></i>
            </span>
          ) : (
            <span
              className="position-absolute bottom-0 end-0 badge rounded-circle bg-secondary shadow p-2"
              style={{ fontSize: '0.875rem' }}
            >
              <i className="bi bi-lock-fill"></i>
            </span>
          )}
        </div>

        {/* Achievement Details */}
        <h6 className="fw-bold mb-2">{achievement.name}</h6>

        {isUnlocked && achievement.unlockedAt && (
          <div className="text-success mb-2">
            <i className="bi bi-check-circle-fill me-1"></i>
            <small>{formatDate(achievement.unlockedAt)}</small>
          </div>
        )}

        <p className="text-muted small mb-3">{achievement.description}</p>

        {/* Unlocked Features */}
        {achievement.unlockedFeatures.length > 0 && (
          <div className="mt-3 pt-3 border-top">
            <div className="text-muted small mb-2">
              <strong>{labels.unlocks}:</strong>
            </div>
            <div className="d-flex flex-wrap gap-1 justify-content-center">
              {achievement.unlockedFeatures.map((feature) => (
                <span
                  key={feature.key}
                  className={`badge ${isUnlocked ? 'bg-success' : 'bg-secondary bg-opacity-75'} text-white`}
                >
                  <i className="bi bi-unlock-fill me-1"></i>
                  {feature.name}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export default AchievementsPage;
